import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";

const inr = (paise) => paise == null ? null
  : "₹" + (Number(paise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Stored UTC, shown IST — the business reads these in local time.
const ist = (iso) => new Date(iso).toLocaleString("en-IN", {
  timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
  hour: "2-digit", minute: "2-digit",
});

export default function LeadTimeline({ leadId, canManage, onChanged }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.get(`/admin/leads/${leadId}/timeline`)
    .then(({ data }) => { setEntries(data.entries); setError(null); })
    .catch(err => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leadId]);

  const voidEntry = async (e) => {
    const reason = window.prompt(
      "Void this entry?\n\nIt stays visible in the timeline, struck through. " +
      "The contractor's stage ceiling recalculates, freeing them to file correctly.\n\nReason:");
    if (reason === null) return;
    try {
      await api.post(`/admin/lead-updates/${e.id}/void`, { reason });
      await load();
      onChanged?.();
    } catch (err) {
      window.alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (entries === null) {
    if (error) {
      return <p className="text-sm text-rose-600 bg-rose-50 rounded-xl p-4">Could not load the work timeline: {error}</p>;
    }
    return <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  }
  if (!entries.length) return <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">No updates yet.</p>;

  return (
    <div className="space-y-3" data-testid="lead-timeline">
      {entries.map(e => {
        const voided = !!e.voided_at;
        const what = e.kind === "stage" ? (e.stage_name || "Stage") : e.pipeline_status;
        const bits = [
          e.percent_complete != null ? `${e.percent_complete}%` : null,
          inr(e.amount_paid_paise) ? `${inr(e.amount_paid_paise)} received` : null,
          e.project_status === "completed" ? "marked complete" : null,
        ].filter(Boolean);
        return (
          <div key={e.id} className={`border-l-2 pl-3 ${voided ? "border-slate-200" : "border-emerald-300"}`}
               data-testid={`timeline-entry-${e.id}`}>
            <div className="text-xs text-slate-500">{ist(e.created_at)}</div>
            <div className="text-sm">
              <span className="font-medium text-slate-900">{e.partner_name}</span>
              {e.scope_label && <span className="text-slate-500"> · {e.scope_label}</span>}
              <span className={`ml-2 ${voided ? "line-through text-slate-400" : "text-slate-800"}`}>{what}</span>
              {bits.length > 0 && (
                <span className={voided ? "text-slate-400 line-through" : "text-slate-600"}> · {bits.join(" · ")}</span>
              )}
            </div>
            {e.note && <div className={`text-sm mt-0.5 ${voided ? "text-slate-400 line-through" : "text-slate-600"}`}>“{e.note}”</div>}
            {voided && (
              <div className="text-xs text-rose-600 mt-0.5">
                └ voided {ist(e.voided_at)} · {e.void_reason}
              </div>
            )}
            {!voided && canManage && (
              <button onClick={() => voidEntry(e)}
                      className="text-xs text-rose-600 hover:underline mt-1"
                      data-testid={`void-entry-${e.id}`}>Void this entry</button>
            )}
          </div>
        );
      })}
    </div>
  );
}
